import { CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { PdvSession, PdvSessionReport } from "@models/pdv-session";
import { PdvSessionsService } from "@services/pdv-sessions.service";
import { hasInputChanged } from "@shared/helpers/hasChanges/hasChanges.helper";
import { EMPTY, catchError, finalize, merge, take, tap } from "rxjs";
import { PdvTerminalSalesComponent } from "./pdv-terminal-sales.component";
import { TableModule } from "primeng/table";
import { ButtonComponent } from "../../../../shared/components/button/button.component";

@Component({
  selector: "app-pdv-sessions-tab",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PdvTerminalSalesComponent,
    TableModule,
    ButtonComponent,
  ],
  templateUrl: "./pdv-sessions-tab.component.html",
  styleUrl: "./pdv-panel.scss",
})
export class PdvSessionsTabComponent implements OnChanges {
  @Input({ required: true }) eventId!: number;
  private readonly service = inject(PdvSessionsService);
  private readonly destroyRef = inject(DestroyRef);
  private listRequest = 0;
  private reportRequest = 0;
  readonly sessions = signal<PdvSession[]>([]);
  readonly loading = signal(false);
  readonly error = signal("");
  readonly report = signal<PdvSessionReport | null>(null);
  readonly phase = new FormControl("all", { nonNullable: true });
  readonly operator = new FormControl("all", { nonNullable: true });
  readonly state = new FormControl("all", { nonNullable: true });
  readonly phaseNames: Record<string, string> = {
    advance: "Antecipados",
    portaria: "Portaria",
  };
  readonly statusNames: Record<string, string> = {
    open: "Aberto",
    closing_pending: "Fechamento pendente",
    closed: "Fechado",
    force_closed: "Encerrado automaticamente",
  };
  readonly paymentNames: Record<string, string> = {
    credit: "Cartão de crédito",
    credit_card: "Cartão de crédito",
    debit: "Cartão de débito",
    pix: "PIX",
    pix_stone: "PIX",
    cash: "Dinheiro",
  };

  constructor() {
    merge(
      this.phase.valueChanges,
      this.operator.valueChanges,
      this.state.valueChanges,
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.clearReport()),
      )
      .subscribe();
  }

  private clearReport() {
    this.reportRequest++;
    this.report.set(null);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.verifyEventChange(changes);
  }
  private verifyEventChange(changes: SimpleChanges) {
    if (
      hasInputChanged<PdvSessionsTabComponent, "eventId">(changes, "eventId")
    ) {
      this.sessions.set([]);
      this.clearReport();
      this.load();
    }
  }
  get filtered() {
    return this.sessions().filter(
      (row) =>
        (this.phase.value === "all" || row.sales_phase === this.phase.value) &&
        (this.operator.value === "all" ||
          String(row.operator_id) === this.operator.value) &&
        (this.state.value === "all" || row.status === this.state.value),
    );
  }
  get operators() {
    return [
      ...new Map(
        this.sessions().map((row) => [row.operator_id, row.operator_name]),
      ).entries(),
    ];
  }
  get totalCents() {
    return this.filtered.reduce((sum, row) => sum + row.sales_total_cents, 0);
  }
  load() {
    if (!this.eventId) return;
    const request = ++this.listRequest;
    this.clearReport();
    this.loading.set(true);
    this.error.set("");
    this.service
      .list(this.eventId)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        tap((rows) => {
          if (request === this.listRequest) this.sessions.set(rows);
        }),
        catchError(() => {
          if (request === this.listRequest)
            this.error.set(
              "Não foi possível carregar os caixas. Tente novamente.",
            );
          return EMPTY;
        }),
        finalize(() => {
          if (request === this.listRequest) this.loading.set(false);
        }),
      )
      .subscribe();
  }
  openReport(row: PdvSession) {
    this.clearReport();
    this.error.set("");
    const request = this.reportRequest;
    this.service
      .report(this.eventId, row.id)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        tap((report) => {
          if (request === this.reportRequest) this.report.set(report);
        }),
        catchError(() => {
          if (request === this.reportRequest)
            this.error.set(
              "Não foi possível carregar o relatório deste caixa.",
            );
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
